<template>
  <v-slide-y-transition>
    <v-card
      v-if="action"
      variant="outlined"
      rounded="lg"
      class="ai-confirm-card mx-3 mb-3"
    >
      <v-card-title class="text-body-2 font-weight-bold d-flex align-center ga-2 pt-3 pb-1 px-3">
        <span
          class="ai-confirm-dot"
          style="background: rgb(var(--v-theme-primary))"
        />
        {{ t('ai-assistant.confirm.title') }}
      </v-card-title>

      <v-card-text class="px-3 pt-1 pb-2">
        <!-- Field name -->
        <div class="d-flex align-center mb-2">
          <span
            class="text-caption text-disabled mr-2"
            style="min-width: 64px"
          >
            {{ t('ai-assistant.confirm.field') }}
          </span>
          <span class="text-caption font-weight-medium">{{ action.label }}</span>
        </div>

        <!-- Current value -->
        <div class="d-flex align-center mb-3">
          <span
            class="text-caption text-disabled mr-2"
            style="min-width: 64px"
          >
            {{ t('ai-assistant.confirm.current') }}
          </span>
          <span
            v-if="isColorField && isValidHex(String(action.currentValue))"
            class="ai-confirm-swatch mr-1"
            :style="{ background: String(action.currentValue) }"
          />
          <span class="text-caption text-mono">
            {{ action.currentValue != null ? displayValue(action.currentValue) : '—' }}
          </span>
        </div>

        <!-- Editable proposed value — country uses a select, colours show a swatch, rest is text -->
        <v-select
          v-if="isCountryField"
          v-model="editedValue"
          density="compact"
          variant="outlined"
          rounded="lg"
          hide-details
          :label="t('ai-assistant.confirm.new-value')"
          :items="COUNTRY_ITEMS"
          item-title="title"
          item-value="value"
          class="ai-confirm-input"
        />
        <v-text-field
          v-else
          v-model="editedValue"
          density="compact"
          variant="outlined"
          rounded="lg"
          hide-details
          :label="t('ai-assistant.confirm.new-value')"
          class="ai-confirm-input"
        >
          <template
            v-if="isColorField"
            #prepend-inner
          >
            <div
              class="ai-confirm-swatch"
              :style="{ background: isValidHex(editedValue) ? editedValue : 'rgba(0,0,0,0.12)' }"
            />
          </template>
        </v-text-field>
      </v-card-text>

      <v-card-actions class="px-3 pb-3 pt-0">
        <v-btn
          variant="text"
          size="small"
          @click="aiStore.rejectPendingAction()"
        >
          {{ t('ai-assistant.confirm.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          variant="flat"
          size="small"
          color="primary"
          rounded="lg"
          :disabled="!editedValue.trim()"
          @click="handleConfirm"
        >
          <v-icon
            start
            size="14"
          >
            mdi-check
          </v-icon>
          {{ t('ai-assistant.confirm.confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-slide-y-transition>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAiAssistantStore } from '@/stores/aiAssistant'

const { t } = useI18n()
const aiStore = useAiAssistantStore()
const { pendingAction: action } = storeToRefs(aiStore)

const editedValue = ref<string>('')

// Pre-fill the input whenever a new action is proposed
watch(
  action,
  (val) => {
    if (val) editedValue.value = String(val.proposedValue ?? '')
  },
  { immediate: true }
)

const COUNTRY_LABELS: Record<string, string> = { de: 'Germany', at: 'Austria', ch: 'Switzerland' }
const COUNTRY_ITEMS = [
  { title: 'Germany', value: 'de' },
  { title: 'Austria', value: 'at' },
  { title: 'Switzerland', value: 'ch' },
]

const isColorField = computed(
  () => /colou?r/i.test(action.value?.field ?? '') || /colou?r/i.test(action.value?.label ?? '')
)

const isCountryField = computed(() => action.value?.field === 'country')

const isValidHex = (val: string) => /^#[0-9A-Fa-f]{6}$/.test(val)

const displayValue = (val: unknown): string =>
  isCountryField.value ? (COUNTRY_LABELS[String(val)] ?? String(val)) : String(val)

const handleConfirm = () => {
  aiStore.confirmPendingAction(editedValue.value)
}
</script>

<style scoped>
.ai-confirm-card {
  border-color: rgba(var(--v-theme-primary), 0.35) !important;
}

.ai-confirm-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ai-confirm-swatch {
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}

.ai-confirm-input :deep(.v-field) {
  border-radius: 10px;
}
</style>
